import { useMutation } from "@apollo/client";
import type { CreateCustomerInput, UpdateCustomerInput } from "@/gql/graphql";
import {
  CREATE_CUSTOMER_MUTATION,
  CUSTOMERS_QUERY,
  DELETE_CUSTOMER_MUTATION,
  UPDATE_CUSTOMER_MUTATION,
} from "@/graphql/customers";
import { useAsyncAction } from "@/lib/hooks/use-async-action";

export const useCreateCustomer = () => {
  const [createMutation] = useMutation(CREATE_CUSTOMER_MUTATION);

  return useAsyncAction(
    async (salonId: string, input: CreateCustomerInput) => {
      const result = await createMutation({
        variables: { salonId, input },
        refetchQueries: [CUSTOMERS_QUERY],
        errorPolicy: "none", // This makes Apollo throw on GraphQL errors
      });

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }

      if (!result.data?.createCustomer) {
        throw new Error("Customer creation failed - no data returned");
      }

      return result.data.createCustomer;
    },
    {
      loadingMessage: "Creating customer...",
      successMessage: "Customer created successfully!",
      errorMessage: "Failed to create customer",
    }
  );
};

export const useUpdateCustomer = () => {
  const [updateMutation] = useMutation(UPDATE_CUSTOMER_MUTATION);

  return useAsyncAction(
    async (salonId: string, customerId: string, input: UpdateCustomerInput) => {
      const result = await updateMutation({
        variables: { salonId, customerId, input },
        refetchQueries: [CUSTOMERS_QUERY],
        errorPolicy: "none", // This makes Apollo throw on GraphQL errors
      });

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }

      if (!result.data?.updateCustomer) {
        throw new Error("Customer update failed - no data returned");
      }

      return result.data.updateCustomer;
    },
    {
      loadingMessage: "Updating customer...",
      successMessage: "Customer updated successfully!",
      errorMessage: "Failed to update customer",
    }
  );
};

export const useDeleteCustomer = () => {
  const [deleteMutation] = useMutation(DELETE_CUSTOMER_MUTATION);

  return useAsyncAction(
    async (salonId: string, customerId: string) => {
      const result = await deleteMutation({
        variables: { salonId, customerId },
        refetchQueries: [CUSTOMERS_QUERY],
        errorPolicy: "none", // This makes Apollo throw on GraphQL errors
      });

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }

      if (!result.data?.deleteCustomer) {
        throw new Error("Customer deletion failed - no data returned");
      }

      return result.data.deleteCustomer;
    },
    {
      loadingMessage: "Deleting customer...",
      successMessage: "Customer deleted successfully!",
      errorMessage: "Failed to delete customer",
    }
  );
};

// Dialog versions without toasts (for use in dialogs that handle their own error display)
export const useCreateCustomerSilent = () => {
  const [createMutation] = useMutation(CREATE_CUSTOMER_MUTATION);

  return useAsyncAction(
    async (salonId: string, input: CreateCustomerInput) => {
      const result = await createMutation({
        variables: { salonId, input },
        refetchQueries: [CUSTOMERS_QUERY],
        errorPolicy: "none",
      });

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }

      if (!result.data?.createCustomer) {
        throw new Error("Customer creation failed - no data returned");
      }

      return result.data.createCustomer;
    },
    {
      showSuccessToast: false, // Disable success toast
      showErrorToast: false, // Disable error toast
      showLoadingToast: false, // Disable loading toast
    }
  );
};

export const useUpdateCustomerSilent = () => {
  const [updateMutation] = useMutation(UPDATE_CUSTOMER_MUTATION);

  return useAsyncAction(
    async (salonId: string, customerId: string, input: UpdateCustomerInput) => {
      const result = await updateMutation({
        variables: { salonId, customerId, input },
        refetchQueries: [CUSTOMERS_QUERY],
        errorPolicy: "none",
      });

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }

      if (!result.data?.updateCustomer) {
        throw new Error("Customer update failed - no data returned");
      }

      return result.data.updateCustomer;
    },
    {
      showSuccessToast: false,
      showErrorToast: false,
      showLoadingToast: false,
    }
  );
};
